// skills/admin/admin.js
// Admin panel logic for dev testing

import {
    DEFAULT_ADMIN_STATE,
    MIN_LEVEL,
    MAX_LEVEL,
    MAX_VIRTUAL_LEVEL,
    SPEED_PRESETS,
    XP_PRESETS,
    CORRUPTION_CHANCE_PRESETS
} from './adminData.js';
import { gameState } from '../../game/gameState.js';
import { addXP, getLevelFromXP } from '../../systems/experience.js';
import { SKILLS, XP_TABLE, GAME_CONFIG, FARM_CONFIG, BLOOMHOLD_CONFIG } from '../../game/constants.js';
import { farmingSkill } from '../farming/farming.js';
import { isBloomholdEligible } from '../../systems/bloomhold.js';
import {
    forceActivateCorruption,
    deactivateCorruption,
    getCorruptionState,
    CORRUPTION_CONFIG
} from '../../systems/corruption.js';
import logger from '../../utils/logger.js';

class AdminPanel {
    constructor() {
        this.state = JSON.parse(JSON.stringify(DEFAULT_ADMIN_STATE));
    }

    // ==================== Level/XP Manipulation ====================

    addLevels(skillId, levels) {
        const skill = gameState.getSkill(skillId);
        if (!skill) return { success: false, reason: 'Skill not found' };

        // Use virtual level for calculations when virtual levels is enabled
        const virtualEnabled = gameState.settings.virtualLevelsEnabled;
        const currentLevel = virtualEnabled ? skill.virtualLevel : skill.level;
        const maxLevel = virtualEnabled ? MAX_VIRTUAL_LEVEL : MAX_LEVEL;
        const newLevel = Math.max(MIN_LEVEL, Math.min(maxLevel, currentLevel + levels));
        return this.setLevel(skillId, newLevel);
    }

    setLevel(skillId, level) {
        const skill = gameState.getSkill(skillId);
        if (!skill) return { success: false, reason: 'Skill not found' };

        const virtualEnabled = gameState.settings.virtualLevelsEnabled;
        const maxLevel = virtualEnabled ? MAX_VIRTUAL_LEVEL : MAX_LEVEL;
        const clampedLevel = Math.max(MIN_LEVEL, Math.min(maxLevel, level));
        const targetXP = XP_TABLE[clampedLevel - 1];

        skill.xp = targetXP;
        // Real level caps at 99, virtual level can go to 120
        skill.level = Math.min(clampedLevel, MAX_LEVEL);
        skill.virtualLevel = clampedLevel;

        // Calculate XP progress for next level
        if (clampedLevel < maxLevel) {
            const currentLevelXP = XP_TABLE[clampedLevel - 1];
            const nextLevelXP = XP_TABLE[clampedLevel];
            skill.currentXp = 0;
            skill.nextLevelXp = nextLevelXP - currentLevelXP;
        } else {
            skill.currentXp = 0;
            skill.nextLevelXp = 0;
        }

        gameState.recalculateTotalLevel();
        this.updateActiveStatus();

        logger.debug(`[ADMIN] Set ${skillId} to level ${clampedLevel}${clampedLevel > MAX_LEVEL ? ' (virtual)' : ''}`);
        return { success: true, newLevel: clampedLevel };
    }

    addXP(skillId, amount) {
        const skill = gameState.getSkill(skillId);
        if (!skill) return { success: false, reason: 'Skill not found' };

        const result = addXP(skillId, amount);
        this.updateActiveStatus();

        logger.debug(`[ADMIN] Added ${amount} XP to ${skillId}`);
        return { success: true, ...result };
    }

    // ==================== Time Skip ====================

    skipTime(minutes) {
        let didSkip = false;
        let actionsCompleted = 0;

        // Skip time for active action (traditional skills)
        const action = gameState.getCurrentAction();
        if (action) {
            // Calculate how many actions would complete in this time
            const msToSkip = minutes * 60 * 1000;
            const actionDuration = action.duration || 3000; // Default 3s if not specified
            const actionsToComplete = Math.floor(msToSkip / actionDuration);

            if (actionsToComplete > 0) {
                const result = this.completeActions(actionsToComplete);
                actionsCompleted = result.actionsCompleted || 0;
                didSkip = true;
                logger.debug(`[ADMIN] Skipped ${minutes} minutes (~${actionsToComplete} actions)`);
            }
        }

        // Skip time for passive architecting systems
        const blueprintResult = this.skipBlueprintTime(minutes);
        if (blueprintResult.success) didSkip = true;

        const milestoneResult = this.skipMilestoneTime(minutes);
        if (milestoneResult.success) didSkip = true;

        const civicResult = this.skipCivicContractTime(minutes);
        if (civicResult.success) didSkip = true;

        if (!didSkip) {
            return { success: false, reason: 'No action in progress' };
        }

        return { success: true, actionsCompleted };
    }

    completeActions(count = 1) {
        let completed = 0;

        for (let i = 0; i < count; i++) {
            // RE-FETCH action each iteration to handle auto-restart
            const action = gameState.getCurrentAction();
            if (!action || !action.tick) break;

            // Use forceComplete flag to bypass normal tick recalculation
            // This triggers immediate completion without elapsed time check
            action.forceComplete = true;
            action.tick();
            completed++;
        }

        logger.debug(`[ADMIN] Completed ${completed} actions`);
        return { success: true, actionsCompleted: completed };
    }

    // ==================== Offline Simulation ====================

    async simulateOfflineTime(minutes) {
        const currentAction = gameState.getCurrentAction();

        // Dynamically import offline progress system
        const { calculateOfflineProgress, applyOfflineProgress } = await import('../../systems/offlineProgress.js');
        const { showOfflineModal } = await import('../../ui/offlineModal.js');

        // Advance farm plot timers to simulate growth during offline time
        // This moves plantedAt back so plots appear to have been growing
        this.skipPlotTime(minutes);

        // Advance architecting timers (blueprints, milestones, civic contracts)
        this.skipBlueprintTime(minutes);
        this.skipMilestoneTime(minutes);
        this.skipCivicContractTime(minutes);

        // Calculate fake timestamp as if we saved X minutes ago
        const fakeTimestamp = Date.now() - (minutes * 60 * 1000);

        // Calculate offline progress (farm plots will now show correct completion)
        const progress = calculateOfflineProgress(fakeTimestamp, currentAction);

        if (!progress) {
            logger.debug('[ADMIN] Offline simulation: not enough time or no progress');
            return { success: false, reason: 'Not enough time elapsed for offline progress' };
        }

        if (!progress.hadAction || progress.actionsCompleted === 0) {
            // Still show the modal even with no actions
            showOfflineModal(progress);
            logger.debug('[ADMIN] Offline simulation: no active action');
            return { success: true, progress };
        }

        // Apply the progress
        const appliedProgress = applyOfflineProgress(progress);

        // Show the offline modal
        showOfflineModal(appliedProgress);

        logger.debug(`[ADMIN] Simulated ${minutes} minutes offline: ${appliedProgress.actionsCompleted} actions`);
        return { success: true, progress: appliedProgress };
    }

    // ==================== Speed Multipliers ====================

    setGlobalSpeed(multiplier) {
        const clampedMultiplier = Math.max(1, Math.min(SPEED_PRESETS[SPEED_PRESETS.length - 1], multiplier));
        this.state.global.speedMultiplier = clampedMultiplier;
        this.updateActiveStatus();

        logger.debug(`[ADMIN] Set global speed to ${clampedMultiplier}x`);
        return { success: true, multiplier: clampedMultiplier };
    }

    setSkillSpeed(skillId, multiplier) {
        if (!this.state.skillOverrides[skillId]) {
            this.state.skillOverrides[skillId] = {};
        }
        this.state.skillOverrides[skillId].speedMultiplier = multiplier;
        this.updateActiveStatus();

        logger.debug(`[ADMIN] Set ${skillId} speed to ${multiplier}x`);
        return { success: true };
    }

    getEffectiveSpeed(skillId) {
        const globalSpeed = this.state.global.speedMultiplier;
        const skillOverride = this.state.skillOverrides[skillId]?.speedMultiplier;

        return skillOverride !== undefined ? skillOverride : globalSpeed;
    }

    // ==================== XP Multipliers ====================

    setGlobalXpMultiplier(multiplier) {
        const clampedMultiplier = Math.max(0.1, Math.min(XP_PRESETS[XP_PRESETS.length - 1], multiplier));
        this.state.global.xpMultiplier = clampedMultiplier;
        this.updateActiveStatus();

        logger.debug(`[ADMIN] Set global XP multiplier to ${clampedMultiplier}x`);
        return { success: true, multiplier: clampedMultiplier };
    }

    setSkillXpMultiplier(skillId, multiplier) {
        if (!this.state.skillOverrides[skillId]) {
            this.state.skillOverrides[skillId] = {};
        }
        this.state.skillOverrides[skillId].xpMultiplier = multiplier;
        this.updateActiveStatus();

        logger.debug(`[ADMIN] Set ${skillId} XP multiplier to ${multiplier}x`);
        return { success: true };
    }

    setNodeXpOverride(skillId, nodeId, xp) {
        const key = `${skillId}:${nodeId}`;
        this.state.nodeXpOverrides[key] = xp;
        this.updateActiveStatus();

        logger.debug(`[ADMIN] Set ${key} XP to ${xp}`);
        return { success: true };
    }

    clearNodeXpOverride(skillId, nodeId) {
        const key = `${skillId}:${nodeId}`;
        delete this.state.nodeXpOverrides[key];
        this.updateActiveStatus();

        return { success: true };
    }

    getEffectiveXp(skillId, nodeId, baseXp) {
        // Check for specific node override first
        const nodeKey = `${skillId}:${nodeId}`;
        if (this.state.nodeXpOverrides[nodeKey] !== undefined) {
            return this.state.nodeXpOverrides[nodeKey];
        }

        // Apply skill-specific multiplier if exists, otherwise global
        const skillXpMult = this.state.skillOverrides[skillId]?.xpMultiplier;
        const multiplier = skillXpMult !== undefined ? skillXpMult : this.state.global.xpMultiplier;

        // If multiplier is 1, return base XP unchanged
        if (multiplier === 1) {
            return baseXp;
        }

        return Math.floor(baseXp * multiplier);
    }

    // ==================== Tool Speed Overrides ====================

    setToolSpeedOverride(toolId, speed) {
        this.state.toolSpeedOverrides[toolId] = speed;
        this.updateActiveStatus();

        logger.debug(`[ADMIN] Set tool ${toolId} speed to ${speed}`);
        return { success: true };
    }

    clearToolSpeedOverride(toolId) {
        delete this.state.toolSpeedOverrides[toolId];
        this.updateActiveStatus();

        return { success: true };
    }

    getEffectiveToolSpeed(toolId, baseSpeed) {
        if (this.state.toolSpeedOverrides[toolId] !== undefined) {
            return this.state.toolSpeedOverrides[toolId];
        }
        return baseSpeed;
    }

    // ==================== Farm Plot Controls ====================

    /**
     * Skip time for all growing farm plots
     * @param {number} minutes - Minutes to skip
     */
    skipPlotTime(minutes) {
        const plots = gameState.getFarmPlots();
        if (!plots) return { success: false, reason: 'Farm plots not initialized' };

        const msToSkip = minutes * 60 * 1000;
        let affectedPlots = 0;

        for (let i = 0; i < FARM_CONFIG.MAX_PLOTS; i++) {
            const plot = plots[i];
            if (plot && plot.state === 'growing' && plot.plantedAt) {
                // Move plantedAt back in time to simulate time skip
                gameState.updateFarmPlot(i, {
                    plantedAt: plot.plantedAt - msToSkip
                });
                affectedPlots++;
            }
        }

        logger.debug(`[ADMIN] Skipped ${minutes} minutes for ${affectedPlots} plots`);
        return { success: true, affectedPlots };
    }

    /**
     * Complete all growing plots instantly
     */
    completeAllPlots() {
        const plots = gameState.getFarmPlots();
        if (!plots) return { success: false, reason: 'Farm plots not initialized' };

        let completedPlots = 0;

        for (let i = 0; i < FARM_CONFIG.MAX_PLOTS; i++) {
            const plot = plots[i];
            if (plot && plot.state === 'growing') {
                // Set plantedAt far back enough to complete instantly
                gameState.updateFarmPlot(i, {
                    plantedAt: Date.now() - plot.growthDuration - 1000
                });
                completedPlots++;
            }
        }

        logger.debug(`[ADMIN] Completed ${completedPlots} plots`);
        return { success: true, completedPlots };
    }

    // ==================== Pet Contract Controls ====================

    /**
     * Skip time for all active pet contracts
     * @param {number} minutes - Minutes to skip
     */
    skipContractTime(minutes) {
        const pets = gameState.getPets();
        if (!pets || pets.length === 0) return { success: false, reason: 'Pets not initialized' };

        const msToSkip = minutes * 60 * 1000;
        let affectedContracts = 0;

        for (const pet of pets) {
            if (pet && pet.contractId && pet.contractStartedAt) {
                // Move contractStartedAt back in time to simulate time skip
                // Directly modify the pet object (getPets returns references)
                pet.contractStartedAt = pet.contractStartedAt - msToSkip;
                affectedContracts++;
            }
        }

        logger.debug(`[ADMIN] Skipped ${minutes} minutes for ${affectedContracts} pet contracts`);
        return { success: true, affectedContracts };
    }

    /**
     * Complete all active pet contracts instantly
     */
    completeAllContracts() {
        const pets = gameState.getPets();
        if (!pets || pets.length === 0) return { success: false, reason: 'Pets not initialized' };

        let completedContracts = 0;

        for (const pet of pets) {
            if (pet && pet.contractId && pet.contractStartedAt) {
                // Set contractStartedAt far back enough to complete instantly
                // Directly modify the pet object (getPets returns references)
                pet.contractStartedAt = 0; // Set to epoch to ensure completion
                completedContracts++;
            }
        }

        logger.debug(`[ADMIN] Completed ${completedContracts} pet contracts`);
        return { success: true, completedContracts };
    }

    /**
     * Get summary of pet contracts for admin panel
     */
    getPetContractSummary() {
        const pets = gameState.getPets();
        if (!pets || pets.length === 0) return { activeContracts: 0, idlePets: 0 };

        let activeContracts = 0;
        let idlePets = 0;

        for (const pet of pets) {
            if (pet && pet.unlocked) {
                if (pet.contractId && pet.contractStartedAt) {
                    activeContracts++;
                } else {
                    idlePets++;
                }
            }
        }

        return { activeContracts, idlePets };
    }

    /**
     * Unlock all pets
     */
    unlockAllPets() {
        const pets = gameState.getPets();
        if (!pets || pets.length === 0) return { success: false, reason: 'Pets not initialized' };

        let unlockedCount = 0;
        for (const pet of pets) {
            if (pet && !pet.unlocked) {
                pet.unlocked = true;
                unlockedCount++;
            }
        }

        logger.debug(`[ADMIN] Unlocked ${unlockedCount} pets`);
        return { success: true, unlockedCount };
    }

    /**
     * Lock all pets (except first one which is always unlocked)
     */
    lockAllPets() {
        const pets = gameState.getPets();
        if (!pets || pets.length === 0) return { success: false, reason: 'Pets not initialized' };

        let lockedCount = 0;
        for (let i = 0; i < pets.length; i++) {
            const pet = pets[i];
            // Keep first pet (rootling) unlocked as it's the starter pet
            if (pet && pet.unlocked && i > 0) {
                pet.unlocked = false;
                pet.contractId = null;
                pet.contractStartedAt = null;
                lockedCount++;
            }
        }

        logger.debug(`[ADMIN] Locked ${lockedCount} pets`);
        return { success: true, lockedCount };
    }

    /**
     * Reset all pet evolution tiers to 0
     */
    resetAllPetTiers() {
        const pets = gameState.getPets();
        if (!pets || pets.length === 0) return { success: false, reason: 'Pets not initialized' };

        let resetCount = 0;
        for (const pet of pets) {
            if (pet && pet.evolution > 0) {
                pet.evolution = 0;
                resetCount++;
            }
        }

        logger.debug(`[ADMIN] Reset ${resetCount} pet tiers`);
        return { success: true, resetCount };
    }

    /**
     * Reset all pet feed progress
     */
    resetAllPetFeeds() {
        const pets = gameState.getPets();
        if (!pets || pets.length === 0) return { success: false, reason: 'Pets not initialized' };

        let resetCount = 0;
        for (const pet of pets) {
            if (pet && (pet.foodFed || 0) > 0) {
                pet.foodFed = 0;
                resetCount++;
            }
        }

        logger.debug(`[ADMIN] Reset ${resetCount} pet food progress`);
        return { success: true, resetCount };
    }

    /**
     * Set pet evolution tier
     * @param {string} petId - Pet identifier
     * @param {number} tier - Target tier (0, 1, or 2)
     */
    setPetTier(petId, tier) {
        const pets = gameState.getPets();
        if (!pets) return { success: false, reason: 'Pets not initialized' };

        const pet = pets.find(p => p && p.id === petId);
        if (!pet) return { success: false, reason: 'Pet not found' };

        if (tier < 0 || tier > 2) return { success: false, reason: 'Invalid tier (0-2)' };

        pet.evolution = tier;
        pet.unlocked = true; // Ensure unlocked

        logger.debug(`[ADMIN] Set ${petId} to tier ${tier}`);
        return { success: true, petId, tier };
    }

    /**
     * Max out pet food progress
     * @param {string} petId - Pet identifier
     */
    maxPetFeeds(petId) {
        const pets = gameState.getPets();
        if (!pets) return { success: false, reason: 'Pets not initialized' };

        const pet = pets.find(p => p && p.id === petId);
        if (!pet) return { success: false, reason: 'Pet not found' };

        // Max food depends on evolution tier and pet type (multiplier)
        const evolution = pet.evolution || 0;
        const baseFood = { 0: 250, 1: 350, 2: 500 };
        const multipliers = { rootling: 1.0, felltracker: 1.5, ichorebound: 2.0, chimera: 2.5, eidolon: 3.0 };
        const multiplier = multipliers[petId] || 1.0;
        pet.foodFed = Math.floor((baseFood[evolution] || 250) * multiplier);
        pet.unlocked = true;

        logger.debug(`[ADMIN] Maxed food for ${petId} (${pet.foodFed})`);
        return { success: true, petId };
    }

    /**
     * Fill loot chest with test items
     */
    fillLootChest() {
        const testItems = [
            { itemId: 'pyrestone_ore', quantity: 50 },
            { itemId: 'blackiron_ore', quantity: 30 },
            { itemId: 'common_timber', quantity: 40 },
            { itemId: 'ironwood_logs', quantity: 25 },
            { itemId: 'raw_sand_smelt', quantity: 35 },
            { itemId: 'basic_pet_food', quantity: 100 },
            { itemId: 'refined_pet_food', quantity: 50 },
            { itemId: 'condensed_pet_food', quantity: 25 }
        ];

        let addedCount = 0;
        for (const item of testItems) {
            gameState.addLootChestItem(item.itemId, item.quantity);
            addedCount++;
        }

        logger.debug(`[ADMIN] Added ${addedCount} item types to loot chest`);
        return { success: true, addedCount };
    }

    /**
     * Clear loot chest
     */
    clearLootChest() {
        const chest = gameState.getLootChest();
        if (!chest) return { success: false, reason: 'Loot chest not initialized' };

        const itemCount = chest.length;
        gameState.player.lootChest = [];

        logger.debug(`[ADMIN] Cleared ${itemCount} items from loot chest`);
        return { success: true, itemCount };
    }

    /**
     * Get loot chest summary for admin panel
     */
    getLootChestSummary() {
        const chest = gameState.getLootChest();
        if (!chest) return { itemTypes: 0, totalItems: 0 };

        const totalItems = chest.reduce((sum, item) => sum + (item.quantity || 0), 0);
        return { itemTypes: chest.length, totalItems };
    }

    // ==================== Blueprint/Milestone/Civic Controls ====================

    /**
     * Skip time for active blueprint
     * @param {number} minutes - Minutes to skip
     */
    skipBlueprintTime(minutes) {
        const blueprint = gameState.getActiveBlueprint();
        if (!blueprint) return { success: false, reason: 'No active blueprint' };

        const msToSkip = minutes * 60 * 1000;
        // Move startedAt back in time to simulate time skip
        blueprint.startedAt = blueprint.startedAt - msToSkip;

        logger.debug(`[ADMIN] Skipped ${minutes} minutes for blueprint`);
        return { success: true };
    }

    /**
     * Skip time for active milestone
     * @param {number} minutes - Minutes to skip
     */
    skipMilestoneTime(minutes) {
        const milestone = gameState.getActiveMilestone();
        if (!milestone) return { success: false, reason: 'No active milestone' };

        const msToSkip = minutes * 60 * 1000;
        // Move startedAt back in time to simulate time skip
        milestone.startedAt = milestone.startedAt - msToSkip;

        logger.debug(`[ADMIN] Skipped ${minutes} minutes for milestone`);
        return { success: true };
    }

    /**
     * Skip time for active civic contract phase
     * @param {number} minutes - Minutes to skip
     */
    skipCivicContractTime(minutes) {
        const contract = gameState.getActiveCivicContract();
        if (!contract) return { success: false, reason: 'No active civic contract' };

        const msToSkip = minutes * 60 * 1000;
        // Move phaseStartedAt back in time to simulate time skip
        contract.phaseStartedAt = contract.phaseStartedAt - msToSkip;

        logger.debug(`[ADMIN] Skipped ${minutes} minutes for civic contract`);
        return { success: true };
    }

    /**
     * Complete active blueprint instantly
     */
    completeBlueprint() {
        const blueprint = gameState.getActiveBlueprint();
        if (!blueprint) return { success: false, reason: 'No active blueprint' };

        // Set startedAt far back enough to complete instantly
        blueprint.startedAt = Date.now() - blueprint.duration - 1000;

        logger.debug('[ADMIN] Completed blueprint');
        return { success: true };
    }

    /**
     * Complete active milestone instantly
     */
    completeMilestoneBuilding() {
        const milestone = gameState.getActiveMilestone();
        if (!milestone) return { success: false, reason: 'No active milestone' };

        // Set startedAt far back enough to complete instantly
        milestone.startedAt = 0;

        logger.debug('[ADMIN] Completed milestone building');
        return { success: true };
    }

    /**
     * Complete active civic contract phase instantly
     */
    completeCivicContractPhase() {
        const contract = gameState.getActiveCivicContract();
        if (!contract) return { success: false, reason: 'No active civic contract' };

        // Set phaseStartedAt to 0 to trigger completion on next tick
        contract.phaseStartedAt = 0;

        logger.debug('[ADMIN] Completed civic contract phase');
        return { success: true };
    }

    /**
     * Get architecting summary for admin panel
     */
    getArchitectingSummary() {
        const activeBlueprint = gameState.getActiveBlueprint();
        const activeMilestone = gameState.getActiveMilestone();
        const activeCivicContract = gameState.getActiveCivicContract();
        const completedMilestones = gameState.getCompletedMilestones();

        return {
            hasBlueprint: !!activeBlueprint,
            blueprintId: activeBlueprint?.blueprintId,
            hasMilestone: !!activeMilestone,
            milestoneId: activeMilestone?.milestoneId,
            hasCivicContract: !!activeCivicContract,
            contractId: activeCivicContract?.contractId,
            completedMilestonesCount: completedMilestones.length
        };
    }

    // ==================== Bounty Contract Controls ====================

    /**
     * Spawn items required for active bounty contracts
     */
    spawnBountyRequirements() {
        const bountyContracts = gameState.getBountyContracts();
        if (!bountyContracts || bountyContracts.length === 0) {
            return { success: false, reason: 'No active bounty contracts' };
        }

        let itemsSpawned = 0;
        for (const contract of bountyContracts) {
            if (contract.requirements) {
                for (const req of contract.requirements) {
                    if (req.itemId && req.target) {
                        // Spawn exactly the target amount
                        gameState.addItem(req.itemId, req.target);
                        itemsSpawned += req.target;
                    }
                }
            }
        }

        logger.debug(`[ADMIN] Spawned ${itemsSpawned} items for bounty contracts`);
        return { success: true, itemsSpawned };
    }

    /**
     * Clear all active bounty contracts
     */
    clearBountyContracts() {
        const bountyContracts = gameState.getBountyContracts();
        if (!bountyContracts) return { success: false, reason: 'Bounty contracts not initialized' };

        const count = bountyContracts.length;
        gameState.player.bountyContracts = [];

        logger.debug(`[ADMIN] Cleared ${count} bounty contracts`);
        return { success: true, count };
    }

    /**
     * Get bounty contract summary for admin panel
     */
    getBountyContractSummary() {
        const bountyContracts = gameState.getBountyContracts();
        if (!bountyContracts) return { activeCount: 0 };

        return { activeCount: bountyContracts.length };
    }

    /**
     * Clear all farm plots (reset to empty)
     */
    clearAllPlots() {
        const plots = gameState.getFarmPlots();
        if (!plots) return { success: false, reason: 'Farm plots not initialized' };

        let clearedPlots = 0;

        for (let i = 0; i < FARM_CONFIG.MAX_PLOTS; i++) {
            const plot = plots[i];
            if (plot && plot.unlocked && plot.state !== 'empty') {
                farmingSkill.clearPlot(i);
                clearedPlots++;
            }
        }

        logger.debug(`[ADMIN] Cleared ${clearedPlots} plots`);
        return { success: true, clearedPlots };
    }

    /**
     * Unlock all farm plots regardless of level
     */
    unlockAllPlots() {
        const plots = gameState.getFarmPlots();
        if (!plots) return { success: false, reason: 'Farm plots not initialized' };

        let unlockedPlots = 0;

        for (let i = 0; i < FARM_CONFIG.MAX_PLOTS; i++) {
            const plot = plots[i];
            if (plot && !plot.unlocked) {
                gameState.updateFarmPlot(i, { unlocked: true });
                unlockedPlots++;
            }
        }

        logger.debug(`[ADMIN] Unlocked ${unlockedPlots} additional plots`);
        return { success: true, unlockedPlots };
    }

    /**
     * Reset plot locks based on current farming level
     * Locks plots that shouldn't be accessible at current level
     * Clears any growing crops in plots that get locked
     */
    resetPlotLocks() {
        const plots = gameState.getFarmPlots();
        if (!plots) return { success: false, reason: 'Farm plots not initialized' };

        const skill = gameState.getSkill(SKILLS.FARMING);
        let lockedPlots = 0;
        let clearedPlots = 0;

        for (let i = 0; i < FARM_CONFIG.MAX_PLOTS; i++) {
            const plot = plots[i];
            if (!plot) continue;

            const requiredLevel = FARM_CONFIG.UNLOCK_LEVELS[i];
            const shouldBeUnlocked = i < FARM_CONFIG.STARTING_PLOTS || skill.level >= requiredLevel;

            if (!shouldBeUnlocked && plot.unlocked) {
                // Lock this plot
                const updates = { unlocked: false };

                // If plot has a crop growing/ready, clear it
                if (plot.state !== 'empty') {
                    updates.state = 'empty';
                    updates.plantedAt = null;
                    updates.growthDuration = null;
                    updates.cropType = null;
                    updates.cropId = null;
                    updates.cropData = null;
                    clearedPlots++;
                }

                gameState.updateFarmPlot(i, updates);
                lockedPlots++;
            }
        }

        logger.debug(`[ADMIN] Reset plot locks - locked ${lockedPlots} plots, cleared ${clearedPlots} crops`);
        return { success: true, lockedPlots, clearedPlots };
    }

    /**
     * Get farm plots summary for admin display
     */
    getFarmPlotsSummary() {
        const plots = gameState.getFarmPlots();
        if (!plots) return null;

        let empty = 0, growing = 0, ready = 0, locked = 0;

        for (let i = 0; i < FARM_CONFIG.MAX_PLOTS; i++) {
            const plot = plots[i];
            if (!plot) continue;

            if (!plot.unlocked) {
                locked++;
            } else if (plot.state === 'empty') {
                empty++;
            } else if (plot.state === 'growing') {
                growing++;
            } else if (plot.state === 'ready') {
                ready++;
            }
        }

        return { empty, growing, ready, locked, total: FARM_CONFIG.MAX_PLOTS };
    }

    // ==================== BloomHold Controls ====================

    /**
     * Unlock BloomHold without paying gold or meeting level requirement
     */
    unlockBloomhold() {
        if (gameState.isBloomholdUnlocked()) {
            return { success: false, reason: 'BloomHold already unlocked' };
        }

        gameState.player.bloomholdUnlocked = true;
        logger.debug('[ADMIN] Unlocked BloomHold');
        return { success: true };
    }

    /**
     * Lock BloomHold (for testing unlock flow)
     */
    lockBloomhold() {
        if (!gameState.isBloomholdUnlocked()) {
            return { success: false, reason: 'BloomHold already locked' };
        }

        gameState.player.bloomholdUnlocked = false;
        logger.debug('[ADMIN] Locked BloomHold');
        return { success: true };
    }

    /**
     * Clear all items from BloomHold (moves items to bank for easy testing)
     */
    clearBloomhold() {
        const slots = gameState.getBloomholdSlots();
        let movedItems = 0;
        let movedQuantity = 0;

        // Move all items to bank
        for (const slot of slots) {
            gameState.addBankItem(slot.itemId, slot.quantity);
            movedItems++;
            movedQuantity += slot.quantity;
        }

        // Clear BloomHold
        gameState.player.bloomholdSlots = [];

        logger.debug(`[ADMIN] Cleared BloomHold - moved ${movedItems} item types (${movedQuantity} total) to bank`);
        return { success: true, movedItems, movedQuantity };
    }

    /**
     * Add item to BloomHold (only eligible items)
     * @param {string} itemId - Item to add
     * @param {number} quantity - Amount to add
     */
    addToBloomhold(itemId, quantity) {
        if (!gameState.isBloomholdUnlocked()) {
            return { success: false, reason: 'BloomHold not unlocked' };
        }

        if (!isBloomholdEligible(itemId)) {
            return { success: false, reason: 'Item not eligible for BloomHold' };
        }

        const result = gameState.addBloomholdItem(itemId, quantity);
        if (result.success) {
            logger.debug(`[ADMIN] Added ${quantity}x ${itemId} to BloomHold`);
        }
        return result;
    }

    /**
     * Get BloomHold summary for admin display
     */
    getBloomholdSummary() {
        const unlocked = gameState.isBloomholdUnlocked();
        if (!unlocked) {
            return { unlocked: false, usedSlots: 0, maxSlots: BLOOMHOLD_CONFIG.MAX_SLOTS, totalItems: 0 };
        }

        const slots = gameState.getBloomholdSlots();
        return {
            unlocked: true,
            usedSlots: slots.length,
            maxSlots: BLOOMHOLD_CONFIG.MAX_SLOTS,
            totalItems: slots.reduce((sum, slot) => sum + slot.quantity, 0)
        };
    }

    // ==================== Gold/Currency Controls ====================

    /**
     * Add gold (coins) to inventory
     * @param {number} amount - Amount of gold to add
     */
    addGold(amount) {
        if (amount <= 0) return { success: false, reason: 'Amount must be positive' };

        const result = gameState.addItem('coins', amount);
        logger.debug(`[ADMIN] Added ${amount} gold`);
        return result;
    }

    /**
     * Set gold to a specific amount
     * @param {number} amount - Target gold amount
     */
    setGold(amount) {
        if (amount < 0) return { success: false, reason: 'Amount cannot be negative' };

        const currentGold = gameState.getGold();

        if (currentGold > 0) {
            gameState.removeItem('coins', currentGold);
        }

        if (amount > 0) {
            gameState.addItem('coins', amount);
        }

        logger.debug(`[ADMIN] Set gold to ${amount}`);
        return { success: true };
    }

    /**
     * Get current gold amount
     */
    getGold() {
        return gameState.getGold();
    }

    // ==================== Tool Purchase Controls ====================

    /**
     * Reset all purchased tools for a skill or all skills
     * @param {string} [skillId] - Skill to reset, or all if not provided
     */
    resetPurchasedTools(skillId) {
        gameState.resetPurchasedTools(skillId);
        logger.debug(`[ADMIN] Reset purchased tools${skillId ? ` for ${skillId}` : ' for all skills'}`);
        return { success: true };
    }

    /**
     * Grant a tool without payment (for testing)
     * @param {string} skillId - Skill identifier
     * @param {string} toolId - Tool identifier
     */
    grantTool(skillId, toolId) {
        // Add to purchased without cost
        if (!gameState.player.purchasedTools[skillId]) {
            gameState.player.purchasedTools[skillId] = [];
        }

        if (!gameState.player.purchasedTools[skillId].includes(toolId)) {
            gameState.player.purchasedTools[skillId].push(toolId);
            logger.debug(`[ADMIN] Granted ${toolId} for ${skillId}`);
            return { success: true, action: 'granted' };
        }

        return { success: false, reason: 'Already owned' };
    }

    /**
     * Grant all tools for a skill
     * @param {string} skillId - Skill identifier
     * @param {Array} tools - Array of tool objects with id property
     */
    grantAllTools(skillId, tools) {
        let granted = 0;
        for (const tool of tools) {
            const result = this.grantTool(skillId, tool.id);
            if (result.success) granted++;
        }
        logger.debug(`[ADMIN] Granted ${granted} tools for ${skillId}`);
        return { success: true, granted };
    }

    // ==================== Reset Functions ====================

    resetOverrides() {
        this.state = JSON.parse(JSON.stringify(DEFAULT_ADMIN_STATE));

        // Reset farm plot unlock status based on current level
        const plots = gameState.getFarmPlots();
        if (plots) {
            const farmLevel = gameState.getSkill(SKILLS.FARMING).level;
            for (let i = 0; i < FARM_CONFIG.MAX_PLOTS; i++) {
                const plot = plots[i];
                if (plot) {
                    const requiredLevel = FARM_CONFIG.UNLOCK_LEVELS[i];
                    const shouldBeUnlocked = requiredLevel !== undefined && farmLevel >= requiredLevel;
                    gameState.updateFarmPlot(i, { unlocked: shouldBeUnlocked });
                }
            }
        }

        logger.debug('[ADMIN] Reset all overrides to defaults');
        return { success: true };
    }

    resetCharacter() {
        // Clear current action
        gameState.clearAction();

        // Reset all skills to level 1
        for (const skillId of Object.values(SKILLS)) {
            const skill = gameState.getSkill(skillId);
            skill.level = 1;
            skill.virtualLevel = 1;
            skill.xp = 0;
            skill.currentXp = 0;
            skill.nextLevelXp = XP_TABLE[1];
            skill.xpHistory = [];
        }

        // Clear inventory (slot-based)
        gameState.player.inventorySlots = new Array(28).fill(null);

        // Reset purchased tools
        gameState.resetPurchasedTools();

        // Reset BloomHold
        gameState.player.bloomholdUnlocked = false;
        gameState.player.bloomholdSlots = [];

        // Reset stats
        gameState.player.stats.totalLevel = Object.values(SKILLS).length;
        gameState.player.stats.combatLevel = 1;

        logger.debug('[ADMIN] Reset character to fresh state');
        return { success: true };
    }

    // ==================== Corruption Controls ====================

    /**
     * Force activate corruption for testing
     * @param {number} durationSeconds - Duration in seconds (default: 60)
     */
    triggerCorruption(durationSeconds = 60) {
        forceActivateCorruption(durationSeconds * 1000);
        logger.debug(`[ADMIN] Triggered corruption for ${durationSeconds}s`);
        return { success: true, duration: durationSeconds };
    }

    /**
     * End corruption early
     */
    endCorruption() {
        deactivateCorruption();
        logger.debug('[ADMIN] Ended corruption');
        return { success: true };
    }

    /**
     * Get current corruption state
     */
    getCorruptionInfo() {
        return getCorruptionState();
    }

    /**
     * Get corruption config for display
     */
    getCorruptionConfig() {
        return CORRUPTION_CONFIG;
    }

    /**
     * Set corruption proc chance override
     * @param {number|null} chance - Override chance (0-1) or null for default
     */
    setCorruptionProcChance(chance) {
        this.state.corruptionProcChance = chance;
        this.updateActiveStatus();

        if (chance === null) {
            logger.debug('[ADMIN] Reset corruption proc chance to default');
        } else {
            logger.debug(`[ADMIN] Set corruption proc chance to ${(chance * 100).toFixed(0)}%`);
        }
        return { success: true, chance };
    }

    /**
     * Get current corruption proc chance (override or null)
     */
    getCorruptionProcChance() {
        return this.state.corruptionProcChance;
    }

    /**
     * Get corruption chance presets for UI
     */
    getCorruptionChancePresets() {
        return CORRUPTION_CHANCE_PRESETS;
    }

    // ==================== State Helpers ====================

    hasActiveOverrides() {
        if (this.state.global.speedMultiplier !== 1.0) return true;
        if (this.state.global.xpMultiplier !== 1.0) return true;
        if (Object.keys(this.state.skillOverrides).length > 0) return true;
        if (Object.keys(this.state.nodeXpOverrides).length > 0) return true;
        if (Object.keys(this.state.toolSpeedOverrides).length > 0) return true;
        if (this.state.corruptionProcChance !== null) return true;
        return false;
    }

    updateActiveStatus() {
        this.state.isActive = this.hasActiveOverrides();
    }

    getState() {
        return this.state;
    }

    getSkillsList() {
        return Object.entries(SKILLS).map(([key, value]) => ({
            id: value,
            name: key.charAt(0) + key.slice(1).toLowerCase()
        }));
    }
}

export const adminPanel = new AdminPanel();

// Expose to window for console access in dev
if (typeof window !== 'undefined') {
    window.adminPanel = adminPanel;
}

export default adminPanel;
